import { useQuery } from '@apollo/client';
import { faClockRotateLeft, faDatabase, faFileCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { snakeCase } from 'lodash';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import AssistantTabForm, { TabSection } from './AssistantTabForm.tsx';
import ToggleCard from './ToggleCard.tsx';
import {
    DefaultChannelsFields,
    EMPTY_DEFAULT_CHANNELS,
    ErpToolProviderCard,
    renderOptionLabel,
} from './toolProviderCards.tsx';
import { useAssistantConfigForm } from './useAssistantConfigForm.ts';
import Alert from '../../../../core/components/Alert.tsx';
import Button from '../../../../core/components/Button.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../../core/components/Navigation/internal/queries.ts';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';
import { url } from '../../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_ERP_PATH } from '../../../../customer/constants.ts';
import { IChannel } from '../../../../document/stores/channelStore.tsx';
import { canManageIntegrations } from '../../../../users/utils.ts';
import { GET_AVAILABLE_TOOL_PROVIDERS } from '../../queries.ts';
import { isErpToolProvider, ORDER_HISTORY_TOOL_PROVIDER_KEY } from '../config/erpToolProviders.ts';

const ErpIcon = withIcon(faDatabase);
const MasterDataIcon = withIcon(faDatabase);
const OrderHistoryIcon = withIcon(faClockRotateLeft);
const DocumentCreationIcon = withIcon(faFileCirclePlus);

interface AssistantIntegrationsTabProps {
    agent: any;
    onChange: () => Promise<unknown>;
}

/** The Integrations tab: the ERP system, the data lookups and the documents the assistant may create. Same fields and
 *  rules as the agents config's Integrations step; SharePoint and SFTP live on the Knowledge tab. */
const AssistantIntegrationsTab = ({ agent, onChange }: AssistantIntegrationsTabProps) => {
    const { t } = useTranslation('assistants');
    const { user } = useApplicationContext();
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);

    const { data: toolProvidersData } = useQuery(GET_AVAILABLE_TOOL_PROVIDERS);
    const availableToolProviders = toolProvidersData?.availableToolProviders || [];
    const erpToolProviders = availableToolProviders.filter((provider) => isErpToolProvider(provider.key));
    const isOrderHistoryAvailable = availableToolProviders.some(
        (provider) => provider.key === ORDER_HISTORY_TOOL_PROVIDER_KEY
    );

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS);

    const masterDataToolProvider = form.watch('masterDataToolProvider');
    const hasNoIntegrations =
        !form.watch('erpToolProvider') &&
        !masterDataToolProvider &&
        !form.watch('sharepointToolProvider') &&
        !form.watch('sftpKnowledgeSource') &&
        !form.watch('orderHistoryToolProvider');

    return (
        <AssistantTabForm
            title={t('detail.integrationsTab.title')}
            description={t('detail.integrationsTab.description')}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            // the agents step also checks SharePoint and SFTP; they are validated on the Knowledge tab here
            isValid={!masterDataToolProvider || Boolean(masterDataToolProvider.channelId)}
        >
            <TabSection title={t('detail.integrationsTab.erp.title')} titleClassName="text-xs">
                {erpToolProviders.length > 0 ? (
                    <Controller
                        name="erpToolProvider"
                        control={form.control}
                        render={({ field }) => (
                            <>
                                {erpToolProviders.map((provider) => (
                                    <ErpToolProviderCard
                                        key={provider.key}
                                        icon={<ErpIcon />}
                                        providerKey={provider.key}
                                        value={field.value?.key === provider.key ? field.value : null}
                                        // one ERP at a time: switching one on replaces the other
                                        onChange={field.onChange}
                                    />
                                ))}
                            </>
                        )}
                    />
                ) : (
                    <p className="m-0 text-sm text-content-medium">{t('detail.integrationsTab.erp.none')}</p>
                )}
                {canManageIntegrations(user) && (
                    <Button asChild variant="outline" className="self-start">
                        <Link to={url(CUSTOMER_INTEGRATION_ERP_PATH)}>
                            {t('detail.integrationsTab.erp.openSettings')}
                        </Link>
                    </Button>
                )}
            </TabSection>

            <TabSection title={t('detail.integrationsTab.lookups.title')} titleClassName="text-xs">
                <Controller
                    name="masterDataToolProvider"
                    control={form.control}
                    render={({ field }) => (
                        <ToggleCard
                            icon={<MasterDataIcon />}
                            title={t('detail.integrationsTab.lookups.masterData.name')}
                            description={t('detail.integrationsTab.lookups.masterData.description')}
                            isChecked={field.value !== null}
                            onIsCheckedChange={(isChecked) =>
                                field.onChange(isChecked ? { channelId: '', documentType: '' } : null)
                            }
                            panel={
                                field.value && (
                                    <MasterDataChannelField
                                        channelData={channelData}
                                        channelId={field.value.channelId}
                                        onChange={field.onChange}
                                    />
                                )
                            }
                        />
                    )}
                />

                {isOrderHistoryAvailable && (
                    <Controller
                        name="orderHistoryToolProvider"
                        control={form.control}
                        render={({ field }) => (
                            <ToggleCard
                                icon={<OrderHistoryIcon />}
                                title={t('detail.integrationsTab.lookups.orderHistory.name')}
                                description={t('detail.integrationsTab.lookups.orderHistory.description')}
                                isChecked={field.value !== null}
                                onIsCheckedChange={(isChecked) => field.onChange(isChecked ? { enabled: true } : null)}
                            />
                        )}
                    />
                )}
            </TabSection>

            <TabSection title={t('detail.integrationsTab.actions.title')} titleClassName="text-xs">
                <Controller
                    name="defaultChannels"
                    control={form.control}
                    render={({ field }) => (
                        <ToggleCard
                            icon={<DocumentCreationIcon />}
                            title={t('detail.integrationsTab.actions.documentCreation.name')}
                            description={t('detail.integrationsTab.actions.documentCreation.description')}
                            isChecked={field.value !== null}
                            onIsCheckedChange={(isChecked) =>
                                field.onChange(isChecked ? { ...EMPTY_DEFAULT_CHANNELS } : null)
                            }
                            panel={
                                field.value && (
                                    <DefaultChannelsFields
                                        channelData={channelData}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }
                        />
                    )}
                />
            </TabSection>

            {hasNoIntegrations && <Alert severity="warning">{t('detail.integrationsTab.noIntegrationsWarning')}</Alert>}
        </AssistantTabForm>
    );
};

interface MasterDataChannelFieldProps {
    channelData: any;
    channelId: string;
    onChange: (config: { channelId: string; documentType: string }) => void;
}

/** The channel whose master data the assistant looks things up in, grouped by document type like in the agents
 *  config; the group sets the document type that is stored with it. */
const MasterDataChannelField = ({ channelData, channelId, onChange }: MasterDataChannelFieldProps) => {
    const { t } = useTranslation('assistants');
    const { t: tAssistance } = useTranslation('assistance');

    const optionGroups = channelData
        ? Object.entries(channelData)
              .filter(([groupName]) => groupName !== 'agents')
              .map(([groupName, channels]) => {
                  const documentType = groupName.replace(/ProcessingChannels$/i, '');
                  return {
                      label: tAssistance(`overview.navigation.${documentType}`),
                      documentType: snakeCase(documentType).toUpperCase(),
                      options: ((channels as IChannel[]) || []).map((channel) => ({
                          value: channel.id,
                          label: channel.name,
                      })),
                  };
              })
        : [];

    return (
        <FormField label={t('detail.integrationsTab.lookups.masterData.channelLabel')} required>
            <SelectField
                options={[]}
                optionGroups={optionGroups}
                placeholder={t('detail.integrationsTab.lookups.masterData.channelPlaceholder')}
                value={channelId}
                onValueChange={(value) => {
                    const group = optionGroups.find((g) => g.options.some((option) => option.value === value));
                    onChange({ channelId: value, documentType: group?.documentType || '' });
                }}
                renderOption={renderOptionLabel}
                modal
            />
        </FormField>
    );
};

export default AssistantIntegrationsTab;
